import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Search,
    User,
    Ticket,
    AlertTriangle,
    ArrowRight,
    ArrowLeft,
    CheckCircle,
    X,
    MapPin,
    Users,
    FileText,
    Link as LinkIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { useTickets } from '../../contexts/TicketContext';
import { safeParseObject } from '../../utils/storageUtils.js';
import { escalationCategories, availableRoles } from '../../utils/escalationConfig';
import { getStaffDisplayName } from '../../utils/staffDisplay';

const CreateEscalation = () => {
    const navigate = useNavigate();
    const { user: currentStaff, allUsers } = useAuth();
    const staffName = getStaffDisplayName(currentStaff);
    const { tickets, createTicket, getUserTickets } = useTickets();

    // Estados do formulário multi-step
    const [currentStep, setCurrentStep] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchedUser, setSearchedUser] = useState(null);
    const [hasLinkedTicket, setHasLinkedTicket] = useState('no');
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [userRecentTickets, setUserRecentTickets] = useState([]);

    // Dados do ticket de escalação
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedSubcategory, setSelectedSubcategory] = useState('');
    const [description, setDescription] = useState('');
    const [assignedRole, setAssignedRole] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);


    // Buscar usuário pelo Discord ID ou Player ID
    const handleSearchUser = () => {
        if (!searchTerm.trim()) return;

        // Use allUsers from AuthContext instead of localStorage parsing
        // This ensures we're searching the full synchronized user base
        if (!allUsers || !Array.isArray(allUsers)) {
            toast.error('Erro ao carregar lista de usuários. Tente novamente em instantes.');
            return;
        }

        let foundUser = null;
        const term = searchTerm.trim().toLowerCase();

        // Search in allUsers array
        foundUser = allUsers.find(u => {
            // Check Discord ID
            if (u.discordId === term || u.id === term) return true;

            // Check Player ID in any city
            if (u.cities && Array.isArray(u.cities)) {
                return u.cities.some(c => String(c.playerId) === term);
            }
            return false;
        });

        if (foundUser) {
            setSearchedUser(foundUser);

            // Garante que temos um ID para buscar
            const targetId = foundUser.discordId || foundUser.id;

            // Buscar tickets criados pelo usuário (Autor) - AGORA BUSCA TODOS SEM FILTRO DE PERMISSÃO
            const userTickets = targetId ? getUserTickets(targetId) : [];

            setUserRecentTickets(userTickets);
        } else {
            setSearchedUser(null);
            setUserRecentTickets([]);
            alert('Usuário não encontrado. Verifique o ID do Discord ou ID do Personagem.');
        }
    };

    // Avançar para próximo step
    const handleNextStep = () => {
        // Removida a validação que exigia usuário para avançar do passo 1
        if (currentStep === 2 && hasLinkedTicket === 'yes' && !selectedTicket) {
            alert('Por favor, selecione um ticket vinculado.');
            return;
        }
        setCurrentStep(currentStep + 1);
    };

    // Voltar step anterior
    const handlePreviousStep = () => {
        setCurrentStep(currentStep - 1);
    };



    // Publicar escalação
    const handlePublish = async () => {
        // Validações
        if (!selectedCategory || !selectedSubcategory) {
            alert('Por favor, selecione a categoria e o motivo.');
            return;
        }
        if (!description.trim()) {
            alert('Por favor, preencha a descrição detalhada.');
            return;
        }
        if (!assignedRole) {
            alert('Por favor, selecione o departamento responsável.');
            return;
        }

        setIsSubmitting(true);

        try {
            const categoryObj = escalationCategories.find(c => c.id === selectedCategory);

            // Criar ticket de escalação
            const escalationTicket = {
                id: `ESC-${Date.now()}`,
                type: 'escalation',
                escalationType: selectedCategory,
                escalationSubtype: selectedSubcategory,
                title: `[ESCALAÇÃO] ${categoryObj?.label} - ${selectedSubcategory}`,
                description: description,
                userId: searchedUser ? searchedUser.discordId : 'N/A',
                authorName: searchedUser ? searchedUser.name : 'Desconhecido',
                createdBy: currentStaff.discordId,
                createdByName: staffName,
                linkedTicketId: hasLinkedTicket === 'yes' ? selectedTicket?.id : null,
                accessibleBy: ['staff'], // Visível para todos os staffs
                assignedRole: assignedRole, // Departamento responsável
                status: 'escalado', // Status específico para aparecer na aba certa
                category: 'escalacao_interna',
                createdAt: new Date().toISOString(),
                responses: [],
                formData: {
                    characterName: searchedUser?.cities?.[0]?.characterName || (searchedUser ? searchedUser.name : 'Desconhecido'),
                    characterId: searchedUser?.cities?.[0]?.playerId || 'N/A',
                    cities: searchedUser?.cities || []
                }
            };

            // Salvar no contexto
            await createTicket(escalationTicket);

            // Redirecionar para o ticket criado
            navigate(`/staff/tickets/${escalationTicket.id}`);
        } catch (error) {
            console.error('Erro ao criar escalação:', error);
            alert('Erro ao criar escalação. Tente novamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-white flex items-center">
                        <AlertTriangle className="h-8 w-8 mr-3 text-red-400" />
                        Nova Escalação
                    </h1>
                    <p className="text-gray-400 mt-1">Criar ticket interno ou escalação para níveis superiores</p>
                </div>
                <Button
                    variant="outline"
                    onClick={() => navigate('/staff/escalations')}
                    className="bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white border-none shadow-lg shadow-red-500/20"
                >
                    <X className="h-4 w-4 mr-2" />
                    Cancelar
                </Button>
            </div>

            {/* Progress Steps */}
            <Card className="bg-gray-900/30 border-gray-700">
                <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                        {[1, 2, 3, 4].map((step) => (
                            <React.Fragment key={step}>
                                <div className="flex items-center">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${currentStep >= step
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-700 text-gray-400'
                                        }`}>
                                        {currentStep > step ? <CheckCircle className="h-6 w-6" /> : step}
                                    </div>
                                    <div className="ml-3 hidden md:block">
                                        <p className={`text-sm font-medium ${currentStep >= step ? 'text-white' : 'text-gray-500'}`}>
                                            {step === 1 && 'Buscar Usuário'}
                                            {step === 2 && 'Vincular Ticket'}
                                            {step === 3 && 'Detalhes'}
                                            {step === 4 && 'Publicar'}
                                        </p>
                                    </div>
                                </div>
                                {step < 4 && (
                                    <div className={`flex-1 h-1 mx-4 ${currentStep > step ? 'bg-blue-600' : 'bg-gray-700'}`} />
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Step Content */}
            <AnimatePresence mode="wait">
                {/* Step 1: Buscar Usuário */}
                {currentStep === 1 && (
                    <motion.div
                        key="step1"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                    >
                        <Card className="bg-gray-800/50 border-gray-700">
                            <CardHeader>
                                <CardTitle className="text-white flex items-center">
                                    <User className="h-5 w-5 mr-2 text-blue-400" />
                                    Informações do Usuário
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Busca por Discord ID ou Player ID */}
                                <div>
                                    <Label className="text-gray-300 mb-2 block">ID do Discord ou ID do Personagem</Label>
                                    <div className="flex gap-3">
                                        <Input
                                            placeholder="Digite o ID do Discord ou ID do Personagem..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="bg-gray-900/50 border-gray-600 text-white"
                                            onKeyPress={(e) => e.key === 'Enter' && handleSearchUser()}
                                        />
                                        <Button onClick={handleSearchUser} className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 border-none shadow-lg shadow-blue-500/20">
                                            <Search className="h-4 w-4 mr-2" />
                                            Buscar
                                        </Button>
                                    </div>
                                </div>

                                {/* Resultado da Busca */}
                                {searchedUser ? (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="bg-gray-900/50 p-4 rounded-lg border border-green-500/30"
                                    >
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center overflow-hidden">
                                                    {searchedUser.avatar ? (
                                                        <img
                                                            src={`https://cdn.discordapp.com/avatars/${searchedUser.discordId}/${searchedUser.avatar}.png`}
                                                            alt="Avatar"
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <User className="h-6 w-6 text-gray-400" />
                                                    )}
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-white">{searchedUser.name}</h3>
                                                    <p className="text-sm text-gray-400">ID: {searchedUser.discordId}</p>
                                                </div>
                                            </div>
                                            <CheckCircle className="h-6 w-6 text-green-400" />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <p className="text-gray-500 mb-1">Nome do Usuário</p>
                                                <p className="text-white font-medium">{searchedUser.name}</p>
                                            </div>
                                            <div>
                                                <p className="text-gray-500 mb-1">Personagem Principal</p>
                                                <p className="text-white font-medium">
                                                    {searchedUser.cities?.[0]?.characterName || 'N/A'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-gray-500 mb-1">ID do Personagem</p>
                                                <p className="text-white font-medium">
                                                    {searchedUser.cities?.[0]?.playerId || 'N/A'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-gray-500 mb-1">Cidades onde joga</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {searchedUser.cities && searchedUser.cities.length > 0 ? (
                                                        searchedUser.cities.map((city, idx) => (
                                                            <Badge key={idx} className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">
                                                                {city.city}
                                                            </Badge>
                                                        ))
                                                    ) : (
                                                        <span className="text-gray-400">Nenhuma</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ) : (
                                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                                        <div className="flex items-start">
                                            <AlertTriangle className="h-5 w-5 text-yellow-400 mr-3 mt-0.5" />
                                            <div>
                                                <h4 className="text-yellow-400 font-medium mb-1">Nenhum usuário selecionado</h4>
                                                <p className="text-sm text-gray-400">
                                                    Você pode prosseguir sem selecionar um usuário. A escalação será criada como "Desconhecido".
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="flex justify-end">
                                    <Button
                                        onClick={handleNextStep}
                                        className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 border-none shadow-lg shadow-blue-500/20"
                                    >
                                        Próximo
                                        <ArrowRight className="h-4 w-4 ml-2" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* Step 2: Vincular Ticket */}
                {currentStep === 2 && (
                    <motion.div
                        key="step2"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                    >
                        <Card className="bg-gray-800/50 border-gray-700">
                            <CardHeader>
                                <CardTitle className="text-white flex items-center">
                                    <LinkIcon className="h-5 w-5 mr-2 text-purple-400" />
                                    Vincular Ticket Existente
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Pergunta sobre vinculação */}
                                <div>
                                    <Label className="text-gray-300 mb-3 block">Há ticket vinculado a esta escalação?</Label>
                                    <RadioGroup value={hasLinkedTicket} onValueChange={setHasLinkedTicket}>
                                        <div
                                            onClick={() => setHasLinkedTicket('yes')}
                                            className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer transition-all ${hasLinkedTicket === 'yes'
                                                ? 'bg-blue-500/20 border-blue-500'
                                                : 'bg-gray-900/50 border-gray-700 hover:border-gray-600'
                                                }`}
                                        >
                                            <RadioGroupItem value="yes" id="yes" className="border-white text-blue-500" />
                                            <Label htmlFor="yes" className="text-white cursor-pointer flex-1">
                                                Sim, vincular a um ticket existente
                                            </Label>
                                        </div>
                                        <div
                                            onClick={() => setHasLinkedTicket('no')}
                                            className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer transition-all ${hasLinkedTicket === 'no'
                                                ? 'bg-blue-500/20 border-blue-500'
                                                : 'bg-gray-900/50 border-gray-700 hover:border-gray-600'
                                                }`}
                                        >
                                            <RadioGroupItem value="no" id="no" className="border-white text-blue-500" />
                                            <Label htmlFor="no" className="text-white cursor-pointer flex-1">
                                                Não, é uma escalação independente
                                            </Label>
                                        </div>
                                    </RadioGroup>
                                </div>

                                {/* Lista de tickets recentes (se sim) */}
                                {hasLinkedTicket === 'yes' && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar"
                                    >
                                        <Label className="text-gray-300">Selecione o ticket (últimos 5 criados pelo usuário)</Label>
                                        {userRecentTickets.length > 0 ? (
                                            userRecentTickets.map((ticket) => (
                                                <div
                                                    key={ticket.id}
                                                    onClick={() => setSelectedTicket(ticket)}
                                                    className={`bg-gray-900/50 p-4 rounded-lg border cursor-pointer transition-all ${selectedTicket?.id === ticket.id
                                                        ? 'border-blue-500 bg-blue-500/10'
                                                        : 'border-gray-700 hover:border-gray-600'
                                                        }`}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center space-x-3">
                                                            <Ticket className="h-5 w-5 text-gray-400" />
                                                            <div>
                                                                <h4 className="font-medium text-white">{ticket.title}</h4>
                                                                <p className="text-sm text-gray-400">
                                                                    {new Date(ticket.createdAt).toLocaleDateString('pt-BR')} • {ticket.category}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        {selectedTicket?.id === ticket.id && (
                                                            <CheckCircle className="h-5 w-5 text-blue-400" />
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-gray-500 text-center py-4">Este usuário não possui tickets recentes.</p>
                                        )}
                                    </motion.div>
                                )}

                                <div className="flex justify-between">
                                    <Button
                                        onClick={handlePreviousStep}
                                        variant="outline"
                                        className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white border-none shadow-lg shadow-blue-500/20"
                                    >
                                        <ArrowLeft className="h-4 w-4 mr-2" />
                                        Voltar
                                    </Button>
                                    <Button
                                        onClick={handleNextStep}
                                        className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 border-none shadow-lg shadow-blue-500/20"
                                    >
                                        Próximo
                                        <ArrowRight className="h-4 w-4 ml-2" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* Step 3: Detalhes da Escalação */}
                {currentStep === 3 && (
                    <motion.div
                        key="step3"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                    >
                        <Card className="bg-gray-800/50 border-gray-700">
                            <CardHeader>
                                <CardTitle className="text-white flex items-center">
                                    <FileText className="h-5 w-5 mr-2 text-orange-400" />
                                    Detalhes da Escalação
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Categoria */}
                                <div>
                                    <Label className="text-gray-300 mb-3 block">Categoria *</Label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {escalationCategories.map((cat) => (
                                            <div
                                                key={cat.id}
                                                onClick={() => {
                                                    setSelectedCategory(cat.id);
                                                    setSelectedSubcategory('');
                                                }}
                                                className={`bg-gray-900/50 p-4 rounded-lg border cursor-pointer transition-all ${selectedCategory === cat.id
                                                    ? `border-${cat.color}-500 bg-${cat.color}-500/10`
                                                    : 'border-gray-700 hover:border-gray-600'
                                                    }`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-3">
                                                        <cat.icon className={`h-5 w-5 text-${cat.color}-400`} />
                                                        <span className="text-white font-medium">{cat.label}</span>
                                                    </div>
                                                    {selectedCategory === cat.id && (
                                                        <CheckCircle className={`h-5 w-5 text-${cat.color}-400`} />
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Subcategoria */}
                                {selectedCategory && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                    >
                                        <Label className="text-gray-300 mb-2 block">Motivo Específico *</Label>
                                        <Select value={selectedSubcategory} onValueChange={setSelectedSubcategory}>
                                            <SelectTrigger className="bg-gray-900/50 border-gray-600 text-white">
                                                <SelectValue placeholder="Selecione o motivo específico" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-gray-800 border-gray-700 text-white">
                                                {escalationCategories
                                                    .find(c => c.id === selectedCategory)
                                                    ?.subcategories.map((sub, idx) => (
                                                        <SelectItem key={idx} value={sub}>{sub}</SelectItem>
                                                    ))}
                                            </SelectContent>
                                        </Select>
                                    </motion.div>
                                )}

                                {/* Descrição Detalhada */}
                                <div>
                                    <Label className="text-gray-300 mb-2 block">Descrição Detalhada *</Label>
                                    <Textarea
                                        placeholder="Descreva detalhadamente a situação que motivou esta escalação..."
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        className="bg-gray-900/50 border-gray-600 text-white min-h-[150px]"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Seja específico e inclua todas as informações relevantes
                                    </p>
                                </div>

                                <div className="flex justify-between">
                                    <Button
                                        onClick={handlePreviousStep}
                                        variant="outline"
                                        className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white border-none shadow-lg shadow-blue-500/20"
                                    >
                                        <ArrowLeft className="h-4 w-4 mr-2" />
                                        Voltar
                                    </Button>
                                    <Button
                                        onClick={handleNextStep}
                                        disabled={!selectedCategory || !selectedSubcategory || !description.trim()}
                                        className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 border-none shadow-lg shadow-blue-500/20"
                                    >
                                        Próximo
                                        <ArrowRight className="h-4 w-4 ml-2" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* Step 4: Controle de Acesso e Publicação */}
                {currentStep === 4 && (
                    <motion.div
                        key="step4"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                    >
                        <Card className="bg-gray-800/50 border-gray-700">
                            <CardHeader>
                                <CardTitle className="text-white flex items-center">
                                    <Users className="h-5 w-5 mr-2 text-green-400" />
                                    <Users className="h-5 w-5 mr-2 text-green-400" />
                                    Atribuir a Área (Departamento)
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Seleção de Cargos */}
                                <div>
                                    <Label className="text-gray-300 mb-3 block">Departamento Responsável *</Label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {availableRoles.map((role) => (
                                            <div
                                                key={role.value}
                                                onClick={() => setAssignedRole(role.value)}
                                                className={`flex items-center space-x-3 bg-gray-900/50 p-3 rounded-lg border cursor-pointer transition-all ${assignedRole === role.value
                                                    ? 'border-green-500 bg-green-500/10'
                                                    : 'border-gray-700 hover:border-gray-600'
                                                    }`}
                                            >
                                                <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${assignedRole === role.value ? 'border-green-500' : 'border-gray-500'
                                                    }`}>
                                                    {assignedRole === role.value && <div className="w-2 h-2 rounded-full bg-green-500" />}
                                                </div>
                                                <Label
                                                    className="text-white cursor-pointer flex-1"
                                                >
                                                    {role.label}
                                                </Label>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">
                                        Selecione o departamento que será responsável por tratar esta escalação.
                                    </p>
                                </div>

                                {/* Resumo */}
                                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                                    <h4 className="font-bold text-white mb-3 flex items-center">
                                        <CheckCircle className="h-5 w-5 mr-2 text-blue-400" />
                                        Resumo da Escalação
                                    </h4>
                                    <div className="space-y-2 text-sm">
                                        <p className="text-gray-300">
                                            <span className="text-gray-500">Usuário:</span> {searchedUser ? searchedUser.name : 'Desconhecido'}
                                        </p>
                                        <p className="text-gray-300">
                                            <span className="text-gray-500">Categoria:</span> {escalationCategories.find(c => c.id === selectedCategory)?.label}
                                        </p>
                                        <p className="text-gray-300">
                                            <span className="text-gray-500">Motivo:</span> {selectedSubcategory}
                                        </p>
                                        <p className="text-gray-300">
                                            <span className="text-gray-500">Ticket Vinculado:</span> {hasLinkedTicket === 'yes' ? `#${selectedTicket?.id}` : 'Nenhum'}
                                        </p>
                                        <p className="text-gray-300">
                                            <span className="text-gray-500">Departamento:</span> {availableRoles.find(r => r.value === assignedRole)?.label || 'Não selecionado'}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex justify-between">
                                    <Button
                                        onClick={handlePreviousStep}
                                        variant="outline"
                                        className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white border-none shadow-lg shadow-blue-500/20"
                                    >
                                        <ArrowLeft className="h-4 w-4 mr-2" />
                                        Voltar
                                    </Button>
                                    <Button
                                        onClick={handlePublish}
                                        disabled={!assignedRole || isSubmitting}
                                        className="bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 border-none shadow-lg shadow-green-500/20"
                                    >
                                        {isSubmitting ? 'Publicando...' : 'Publicar Escalação'}
                                        <CheckCircle className="h-4 w-4 ml-2" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CreateEscalation;
